import api from "@js/utils/api";
import helpers from "@js/utils/helpers";

/**
 * Currency action to fetch available currencies from Shopify Storefront API
 */
export const runGetCurrency = async (country = 'AU') => {
    try {
        const query = `query @inContext(country: ${country}) {
            localization {
                availableCountries {
                    currency {
                        isoCode
                        name
                        symbol
                    }
                    isoCode
                    name
                    unitSystem
                }
                country {
                    currency {
                        isoCode
                        name
                        symbol
                    }
                    isoCode
                    name
                    unitSystem
                }
            }
        }`;

        const headers = {
            "Content-Type": "application/graphql",
            "X-Shopify-Storefront-Access-Token": api.getStorefrontToken(),
        };

        const response = await fetch(
            `${helpers.getDomain()}/api/2024-07/graphql.json`,
            {
                method: "POST",
                headers: headers,
                body: query,
            }
        );

        const jsonResponse = await response.json();

        if (jsonResponse.data?.localization) {
            const availableCountries = jsonResponse.data.localization.availableCountries;
            const currentCountry = jsonResponse.data.localization.country;
            
            const currencies = availableCountries.map(country => ({
                name: country.currency.isoCode,
                code: country.currency.isoCode,
                symbol: country.currency.symbol,
                fullName: country.currency.name,
                country: country.isoCode,
                countryName: country.name
            }));

            // Dispatch custom event with currency data
            const event = new CustomEvent('currency:fetched', {
                detail: {
                    currencies,
                    currentCountry,
                    success: true
                }
            });
            
            window.dispatchEvent(event);
            
            return {
                success: true,
                currencies,
                currentCountry
            };
        }

        throw new Error('No localization data found');

    } catch (error) {
        console.error('Error fetching currencies:', error);
        
        // Dispatch error event
        const errorEvent = new CustomEvent('currency:error', {
            detail: {
                error: error.message,
                success: false
            }
        });
        
        window.dispatchEvent(errorEvent);
        
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get current currency from cookie
 */
export const getCurrentCurrency = () => {
    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop().split(';').shift();
        }
        return null;
    };

    return getCookie('localization') || 'AU';
};

/**
 * Initialize currency system
 */
export const initializeCurrency = async (defaultCountry = 'AU', currencyCode = 'AUD', currencySymbol = '$') => {
    const currentCountry = getCurrentCurrency() || defaultCountry;
    
    // Fetch available currencies
    const result = await runGetCurrency(currentCountry);
    
    if (result.success) {
        // Set global currency object for backwards compatibility
        window.currency = {
            name: currencyCode,
            symbol: currencySymbol
        };

        // Dispatch initialization complete event
        setTimeout(() => {
            window.dispatchEvent(
                new CustomEvent("currency_initialised", {
                    detail: {
                        currency: result.currentCountry?.currency || {
                            name: currencyCode,
                            symbol: currencySymbol
                        },
                        currencies: result.currencies,
                        country: currentCountry
                    }
                })
            );
        }, 100);
    }
    
    return result;
};

export default runGetCurrency;
